import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyFilterOperator, GalaxyFilterType } from '../enums';
import { GalaxyCheckboxFilterInputComponent } from '../filter-inputs/checkbox-filter-input.component';
import { GalaxyCustomFilterInputComponent } from '../filter-inputs/custom-filter-input.component';
import { GalaxyDateFilterInputComponent } from '../filter-inputs/date-filter-input';
import { GalaxyNoInputFilterInputComponent } from '../filter-inputs/no-input.component';
import { GalaxyNoneSelectedFilterInputComponent } from '../filter-inputs/none-selected-input.component';
import { GalaxyRadioFilterInputComponent } from '../filter-inputs/radio-filter-input.component';
import { GalaxyStringFilterInputComponent } from '../filter-inputs/string-filter-input.component';
import { GalaxyTagsFilterInputComponent } from '../filter-inputs/tags-filter-input.component';
import { GalaxyBooleanFilterInputComponent } from '../filter-inputs/boolean-filter-input.component';
import { GalaxyPhoneFilterInputComponent } from '../filter-inputs/phone-filter-input.component';
import { filterOperatorToLabel } from '../filter-operator-labels';
import {
  GalaxyAbstractFilterInput,
  GalaxyFilterDefinitionInterface,
  GalaxyFilterInterface,
  GalaxyFilterValueInterface,
  InnerGalaxyFilterChipInjectionToken,
} from '../interface';
import { GalaxyFloatFilterInputComponent } from '../filter-inputs/float-filter-input';
import { GalaxyIntegerFilterInputComponent } from '../filter-inputs/integer-filter-input.component';
import { map, of, startWith, switchMap, take } from 'rxjs';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';

export interface AutoCompleteOption {
  value: GalaxyFilterDefinitionInterface;
  type: 'match' | 'divider' | 'non-match';
  disabled?: boolean;
}

enum FilterInputType {
  FILTER_INPUT_TYPE_NO_OPERATOR_SELECTED = 'NoOperatorSelected',
  FILTER_INPUT_TYPE_NO_INPUT = 'NoInputFilter',
  FILTER_INPUT_TYPE_SINGLE_SELECT_STRING = 'SingleSelectStringInput',
  FILTER_INPUT_TYPE_SINGLE_SELECT_TAG = 'SingleSelectTagInput',
  FILTER_INPUT_TYPE_MULTI_SELECT_TAG = 'MultiSelectTagInput',
  FILTER_INPUT_TYPE_DATE_SELECT = 'DateSelectInput',
  FILTER_INPUT_TYPE_MULTI_SELECT_STRING = 'MultiSelectStringInput',
  FILTER_INPUT_TYPE_SINGLE_SELECT_RADIO = 'SingleSelectRadioInput',
  FILTER_INPUT_TYPE_MULTI_SELECT_CHECKBOX = 'MultiSelectCheckboxInput',
  FILTER_INPUT_TYPE_CUSTOM_INPUT = 'CustomInput',
  FILTER_INPUT_TYPE_FLOAT = 'FloatInput',
  FILTER_INPUT_TYPE_BOOLEAN = 'BooleanInput',
  FILTER_INPUT_TYPE_INTEGER = 'IntegerInput',
  FILTER_INPUT_TYPE_PHONE = 'PhoneInput',
}

@Component({
  selector: 'glxy-edit-filter-modal',
  templateUrl: './galaxy-edit-filter-modal.component.html',
  styleUrls: ['./galaxy-edit-filter-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    TranslateModule,
    MatSelectModule,
    MatButtonModule,
    MatInputModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    GalaxyStringFilterInputComponent,
    GalaxyNoInputFilterInputComponent,
    GalaxyNoneSelectedFilterInputComponent,
    GalaxyTagsFilterInputComponent,
    GalaxyDateFilterInputComponent,
    GalaxyNoInputFilterInputComponent,
    GalaxyNoneSelectedFilterInputComponent,
    GalaxyTagsFilterInputComponent,
    GalaxyCheckboxFilterInputComponent,
    GalaxyRadioFilterInputComponent,
    GalaxyCustomFilterInputComponent,
    GalaxyFloatFilterInputComponent,
    GalaxyBooleanFilterInputComponent,
    GalaxyIntegerFilterInputComponent,
    GalaxyPhoneFilterInputComponent,
    MatAutocomplete,
    MatAutocompleteTrigger,
  ],
})
export class GalaxyEditFilterModalComponent implements OnDestroy, OnInit {
  @ViewChild(GalaxyAbstractFilterInput) filterInput!: GalaxyAbstractFilterInput;
  @ViewChild('operatorSelect') operatorSelect?: MatSelect;
  @ViewChild('searchInput') searchInput?: ElementRef;

  @Output() applyFilterChanges = new EventEmitter<GalaxyFilterInterface>();

  FilterInputType = FilterInputType;
  searchControl: FormControl<GalaxyFilterDefinitionInterface | null> =
    new FormControl<GalaxyFilterDefinitionInterface | null>(null);
  searchTerm = toSignal(this.searchControl.valueChanges, { initialValue: null });
  filteredOptions = signal<AutoCompleteOption[]>([]);
  firstFilter: GalaxyFilterDefinitionInterface | null = null;

  selectedOperatorControl: FormControl<GalaxyFilterOperator> = new FormControl<GalaxyFilterOperator>(
    GalaxyFilterOperator.FILTER_OPERATOR_IS,
    { nonNullable: true },
  );

  // this flows in one direction: from the filter inputs into this component
  // attempting to set it within this component will result in it being overwritten
  // if you need to clear the filter input, use the clearValue() method
  filterValues: WritableSignal<GalaxyFilterValueInterface[]> = signal([]);

  _selectedFilter: WritableSignal<GalaxyFilterInterface | null> = signal(null);

  @Input() set selectedFilter(selectedFilter: GalaxyFilterInterface | null) {
    if (selectedFilter) {
      this.selectedOperatorControl.setValue(selectedFilter.operator);
      this.searchControl.disable();
      if (selectedFilter?.definition?.lockedValue) {
        this.selectedOperatorControl.disable();
      }
      this.searchControl.setValue(selectedFilter?.definition ?? null, { emitEvent: true });
    }
    this._selectedFilter.set(selectedFilter);
  }

  filterDefinition = computed(() => this._selectedFilter()?.definition);

  operatorOptions = computed<OperatorOption[]>(() => {
    const supportedOperators = this._selectedFilter()?.definition?.supportedOperators;
    if (supportedOperators === undefined) {
      return [];
    }

    return supportedOperators.map((operator) => ({
      operator: operator,
      label: filterOperatorToLabel[operator],
    }));
  });

  inputType = computed(() => {
    const foundOverride = this.filterInputOverrides().find(
      (override) => override.fieldId === this._selectedFilter()?.fieldId,
    );
    const inputType = this.getInputType(this._selectedFilter(), this.selectedOperator());
    if (foundOverride && inputType !== FilterInputType.FILTER_INPUT_TYPE_NO_INPUT) {
      return FilterInputType.FILTER_INPUT_TYPE_CUSTOM_INPUT;
    }
    return inputType;
  });

  operatorControl = toSignal(this.selectedOperatorControl.valueChanges, {
    initialValue: this.selectedOperatorControl.value,
  });

  selectedOperator = computed(() => this.operatorControl());

  // used to validate that the filter input has been set when applying it
  filterValuesSet = computed(() => {
    return this.filterValues().length > 0;
  });

  // float tolerance will be set to 0.001 if a float filter is present
  filterFloatTolerance = computed(() => {
    const filterValues = this.filterValues();
    if (filterValues?.findIndex((value) => value.float !== undefined) >= 0) {
      return 0.001;
    }
    return undefined;
  });

  private readonly filtersDependencies = inject(InnerGalaxyFilterChipInjectionToken);
  filterInputOverrides = toSignal(this.filtersDependencies.getFilterInputOverrides(), {
    initialValue: [],
  });
  filterOverride = computed(() => {
    return this.filterInputOverrides().find((override) => override.fieldId === this._selectedFilter()?.fieldId);
  });

  constructor() {
    const searchTerm$ = toObservable(this.searchTerm);
    searchTerm$
      .pipe(
        takeUntilDestroyed(),
        startWith(this.searchControl.value),
        switchMap((searchTerm: GalaxyFilterDefinitionInterface | string | null) => {
          let searchString = '';
          if (typeof searchTerm === 'string') {
            searchString = searchTerm;
          } else {
            searchString = searchTerm?.fieldName ?? '';
          }
          return this.filtersDependencies.listObjectFilters(searchString).pipe(
            switchMap((matchingOptions: GalaxyFilterDefinitionInterface[]) => {
              if (!searchString || searchString.length === 0) {
                const matchesOnly: AutoCompleteOption[] = matchingOptions.map((option) => ({
                  value: option,
                  type: 'match' as const,
                }));
                return of(matchesOnly);
              }

              return this.filtersDependencies.listObjectFilters('').pipe(
                map((allOptions: GalaxyFilterDefinitionInterface[]) => {
                  return this.buildAutocompleteOptions(matchingOptions, allOptions);
                }),
              );
            }),
          );
        }),
      )
      .subscribe((options) => {
        this.filteredOptions.set(options);
      });
  }

  ngOnDestroy(): void {
    this._selectedFilter.set(null);
  }

  ngOnInit(): void {
    if (!this._selectedFilter()) {
      this.selectFirstFilterOption();
    }
  }

  private buildAutocompleteOptions(
    matchingOptions: GalaxyFilterDefinitionInterface[],
    allOptions: GalaxyFilterDefinitionInterface[],
  ): AutoCompleteOption[] {
    const matchingIds = new Set(matchingOptions.map((options) => options.fieldId));
    const nonMatchingOptions = allOptions.filter((option) => !matchingIds.has(option.fieldId));

    const result: AutoCompleteOption[] = [
      ...matchingOptions.map((option) => ({ value: option, type: 'match' as const })),
    ];
    if (matchingOptions.length > 0 && nonMatchingOptions.length > 0) {
      result.push({
        value: {} as GalaxyFilterDefinitionInterface,
        type: 'divider',
        disabled: true,
      });
    }
    result.push(...nonMatchingOptions.map((option) => ({ value: option, type: 'non-match' as const })));
    return result;
  }

  applyFilter() {
    const selectedOperator = this.selectedOperator();
    const filterValues = this.filterValues();
    const _filterValuesSet = this.filterValuesSet();
    const filter = this._selectedFilter();
    const floatTolerance = this.filterFloatTolerance();

    const operatorHasNoInput = [
      GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EMPTY,
      GalaxyFilterOperator.FILTER_OPERATOR_IS_EMPTY,
      GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_VALID,
      GalaxyFilterOperator.FILTER_OPERATOR_IS_VALID,
    ].includes(selectedOperator);
    if (
      selectedOperator === GalaxyFilterOperator.FILTER_OPERATOR_INVALID ||
      (!_filterValuesSet && !operatorHasNoInput && !this.filterDefinition()?.preset) // presets are never removed, so they can have an empty value
    ) {
      return;
    }

    // take initial filter and apply delta
    if (filter) {
      filter.operator = selectedOperator;
      filter.values = filterValues;
      filter.floatTolerance = floatTolerance;

      this.applyFilterChanges.emit(filter);
    }
  }

  filterValuesChanged(filterValues: GalaxyFilterValueInterface[]) {
    this.filterValues.set(filterValues);
  }

  private getInputType(filter: GalaxyFilterInterface | null, operator: GalaxyFilterOperator): FilterInputType {
    if (this.filterDefinition()?.type === GalaxyFilterType.FILTER_TYPE_CHECKBOX) {
      if (
        [
          GalaxyFilterOperator.FILTER_OPERATOR_IS,
          GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT,
          GalaxyFilterOperator.FILTER_OPERATOR_IS_EQUAL_TO,
          GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EQUAL_TO,
        ].includes(operator)
      ) {
        return FilterInputType.FILTER_INPUT_TYPE_SINGLE_SELECT_RADIO;
      }
      return FilterInputType.FILTER_INPUT_TYPE_MULTI_SELECT_CHECKBOX;
    }
    if (
      [GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EMPTY, GalaxyFilterOperator.FILTER_OPERATOR_IS_EMPTY].includes(
        operator,
      )
    ) {
      return FilterInputType.FILTER_INPUT_TYPE_NO_INPUT;
    } else if (this.filterDefinition()?.type === GalaxyFilterType.FILTER_TYPE_DATE) {
      return FilterInputType.FILTER_INPUT_TYPE_DATE_SELECT;
    } else if (this.filterDefinition()?.type === GalaxyFilterType.FILTER_TYPE_FLOAT) {
      return FilterInputType.FILTER_INPUT_TYPE_FLOAT;
    } else if (this.filterDefinition()?.type === GalaxyFilterType.FILTER_TYPE_INTEGER) {
      return FilterInputType.FILTER_INPUT_TYPE_INTEGER;
    } else if (
      [
        GalaxyFilterOperator.FILTER_OPERATOR_IS,
        GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT,
        GalaxyFilterOperator.FILTER_OPERATOR_CONTAINS,
        GalaxyFilterOperator.FILTER_OPERATOR_DOES_NOT_CONTAIN,
      ].includes(operator)
    ) {
      switch (this.filterDefinition()?.type) {
        case GalaxyFilterType.FILTER_TYPE_STRING:
          return FilterInputType.FILTER_INPUT_TYPE_SINGLE_SELECT_STRING;
        case GalaxyFilterType.FILTER_TYPE_TAG:
          return FilterInputType.FILTER_INPUT_TYPE_SINGLE_SELECT_TAG;
        case GalaxyFilterType.FILTER_TYPE_BOOLEAN:
          return FilterInputType.FILTER_INPUT_TYPE_BOOLEAN;
        case GalaxyFilterType.FILTER_TYPE_PHONE:
          return FilterInputType.FILTER_INPUT_TYPE_PHONE;
      }
    } else if (
      [
        GalaxyFilterOperator.FILTER_OPERATOR_IS_ANY,
        GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_ANY,
        GalaxyFilterOperator.FILTER_OPERATOR_IS_ALL,
        GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_ALL,
      ].includes(operator)
    ) {
      switch (this.filterDefinition()?.type) {
        case GalaxyFilterType.FILTER_TYPE_TAG:
          return FilterInputType.FILTER_INPUT_TYPE_MULTI_SELECT_TAG;
        case GalaxyFilterType.FILTER_TYPE_STRING:
          return FilterInputType.FILTER_INPUT_TYPE_MULTI_SELECT_STRING;
      }
    }
    if (
      [GalaxyFilterOperator.FILTER_OPERATOR_IS_VALID, GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_VALID].includes(
        operator,
      )
    ) {
      switch (this.filterDefinition()?.type) {
        case GalaxyFilterType.FILTER_TYPE_STRING:
          return FilterInputType.FILTER_INPUT_TYPE_SINGLE_SELECT_STRING;
        case GalaxyFilterType.FILTER_TYPE_PHONE:
          return FilterInputType.FILTER_INPUT_TYPE_NO_INPUT;
      }
    }
    return FilterInputType.FILTER_INPUT_TYPE_NO_OPERATOR_SELECTED;
  }

  protected fieldDisplayName(field: GalaxyFilterDefinitionInterface): string {
    if (!field) {
      return '';
    }
    return field.fieldName ?? '';
  }

  selectNewFilterFromDefinition(filterDefinition: GalaxyFilterDefinitionInterface): void {
    let defaultOperator = GalaxyFilterOperator.FILTER_OPERATOR_IS;
    // Initially set dropdown to first supported operator if available
    if (filterDefinition?.supportedOperators?.[0]) {
      defaultOperator = filterDefinition.supportedOperators[0];
    } else if (filterDefinition?.type === GalaxyFilterType.FILTER_TYPE_TAG) {
      defaultOperator = GalaxyFilterOperator.FILTER_OPERATOR_IS_ALL;
    } else if (filterDefinition?.type === GalaxyFilterType.FILTER_TYPE_FLOAT) {
      defaultOperator = GalaxyFilterOperator.FILTER_OPERATOR_IS_EQUAL_TO;
    }
    const filter: GalaxyFilterInterface = {
      fieldId: filterDefinition.fieldId ?? '',
      operator: defaultOperator,
      definition: filterDefinition,
    };
    this.selectedOperatorControl.setValue(defaultOperator);
    this._selectedFilter.set(filter);
  }

  private selectFirstFilterOption(): void {
    this.filtersDependencies
      .listObjectFilters('')
      .pipe(take(1), startWith([]))
      .subscribe((filters) => {
        const firstFilter = filters?.[0];
        if (firstFilter) {
          this.selectNewFilterFromDefinition(firstFilter);
          this.firstFilter = firstFilter;
        }
        this.searchControl.setValue(null);
      });
  }
}

export interface OperatorOption {
  operator: GalaxyFilterOperator;
  label: string;
}
